import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppStore } from "@/core/store/useAppStore";
import { AudioMediaCard } from "@/components/MediaCard";
import { AudioPlayer } from "@/components/ui/AudioPlayer";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { MediaStage } from "@/core/types/mediaTypes";
import { LibraryLayout, ContentType } from "./LibraryLayout";

/**
 * Audio library view.
 * Wraps LibraryLayout with audio-specific content type filtering
 * and renders AudioMediaCard components in the grid.
 * Opens an AudioPlayer modal when clicking a card.
 */
export const AudioLibrary: React.FC = () => {
  const { selectedClient } = useAppStore();
  const navigate = useNavigate();
  const [selectedAudio, setSelectedAudio] = useState<MediaItem | null>(null);
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);

  const handleContentTypeChange = (contentType: ContentType) => {
    const pathMap: Record<ContentType, string> = {
      images: "../photos",
      "post-videos": "../post-videos",
      stories: "../stories",
      reels: "../reels",
      videos: "../videos",
      audio: "../audio",
      assets: "../assets",
    };
    navigate(pathMap[contentType]);
  };

  const handleAudioClick = (audio: MediaItem) => {
    setSelectedAudio(audio);
    setIsPlayerOpen(true);
  };

  const handlePlayerClose = () => {
    setIsPlayerOpen(false);
    setSelectedAudio(null);
  };

  const renderGrid = (
    items: MediaItem[],
    handleStageChange: (updatedItem: MediaItem, oldStage: MediaStage) => void,
    _handleItemClick?: (item: MediaItem) => void,
    selectionMode?: {
      isActive: boolean;
      selectedIds: number[];
      onToggle: (id: number, event: React.MouseEvent) => void;
    },
  ) => (
    <>
      {items.map((mediaItem) => (
        <AudioMediaCard
          key={mediaItem.id}
          item={mediaItem}
          isSelected={
            selectionMode?.isActive
              ? selectionMode.selectedIds.includes(mediaItem.id)
              : false
          }
          onSelect={
            selectionMode?.isActive
              ? selectionMode.onToggle
              : () => {}
          }
          onClick={() => handleAudioClick(mediaItem)}
          onStageChange={handleStageChange}
        />
      ))}
    </>
  );

  return (
    <LibraryLayout
      contentTypes={["audio"]}
      activeContentType="audio"
      onContentTypeChange={handleContentTypeChange}
      renderGrid={renderGrid}
      extraContent={
        <>
          {selectedAudio && (
            <AudioPlayer
              isOpen={isPlayerOpen}
              onClose={handlePlayerClose}
              src={selectedAudio.getAudioStreamUrl()}
              title={
                selectedAudio.originalFilename?.replace(/\.[^/.]+$/, "") ||
                "Audio"
              }
            />
          )}
        </>
      }
    />
  );
};
